import { Link, useLocation } from "@tanstack/react-router";
import { LogOut, Menu, Search, UserCircle } from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { NavDropdown } from "@/components/layout/NavDropdown";
import { NotificationsPopover } from "@/components/layout/NotificationsPopover";
import { getNavForRole, type PbosRole } from "@/components/layout/nav-items";
import { ProjectSearchPalette } from "@/components/layout/ProjectSearchPalette";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface TopNavBarProps {
  role: PbosRole;
  userName: string | null | undefined;
  userEmail: string | null | undefined;
  userRoleLabel: string | null | undefined;
  unreadCount: number;
  onMobileMenu: () => void;
  onSignOut: () => void;
}

function getInitials(name: string | null | undefined): string {
  if (!name) return "U";
  const parts = name.split(" ").filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
  }
  return (parts[0]?.[0] ?? "U").toUpperCase();
}

function isFlatActive(pathname: string, path: string): boolean {
  if (path === "/") return pathname === "/";
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function TopNavBar({
  role,
  userName,
  userEmail,
  userRoleLabel,
  unreadCount,
  onMobileMenu,
  onSignOut,
}: TopNavBarProps) {
  const items = useMemo(() => getNavForRole(role), [role]);
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const initials = getInitials(userName);

  return (
    <>
      <header
        className="relative z-40 flex h-14 shrink-0 items-center gap-3 border-b border-white/10 px-3 md:px-5"
        style={{ background: "var(--sidebar)", color: "var(--sidebar-foreground)" }}
      >
        <button
          type="button"
          onClick={onMobileMenu}
          className="rounded p-1.5 text-white/85 hover:bg-white/10 md:hidden"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </button>

        <Link to="/" className="flex shrink-0 items-center gap-2.5 pr-1">
          <div className="hidden items-baseline gap-1.5 whitespace-nowrap md:flex">
            <span className="font-mono text-[12px] font-semibold tracking-wider text-white/90">
              BOS
            </span>
            <span className="text-[12px] text-white/25">|</span>
            <span className="text-[12.5px] font-medium text-white/85">Prominent Builders</span>
          </div>
        </Link>

        <div className="mx-1 hidden h-5 w-px shrink-0 bg-white/15 md:block" />

        <nav className="hidden min-w-0 items-center gap-0.5 md:flex">
          {items.map((item) => {
            const hasChildren = item.children && item.children.length > 0;
            const path = item.path ?? "/";
            const active = !hasChildren && isFlatActive(location.pathname, path);
            return (
              <Fragment key={item.label}>
                {item.separatorBefore && (
                  <span aria-hidden="true" className="mx-1.5 h-5 w-px shrink-0 bg-white/15" />
                )}
                {hasChildren ? (
                  <NavDropdown item={item} />
                ) : (
                  <Link
                    to={path}
                    className={cn(
                      "relative flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-[12.5px] font-medium transition-colors",
                      active
                        ? "bg-white/10 text-white"
                        : "text-white/70 hover:bg-white/10 hover:text-white",
                    )}
                  >
                    <span>{item.label}</span>
                    {active && (
                      <span className="absolute -bottom-[1px] left-2 right-2 h-[2px] rounded-full bg-primary" />
                    )}
                  </Link>
                )}
              </Fragment>
            );
          })}
        </nav>

        <div className="flex flex-1 items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="flex items-center gap-2 rounded-md border border-white/15 px-2.5 py-1.5 text-[12.5px] text-white/70 transition-colors hover:bg-white/10"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Search projects and menu…</span>
            <span className="hidden items-center gap-0.5 sm:flex">
              <kbd className="rounded border border-white/10 bg-white/10 px-1 py-px font-mono text-[10px] text-white/70">
                ⌘
              </kbd>
              <kbd className="rounded border border-white/10 bg-white/10 px-1 py-px font-mono text-[10px] text-white/70">
                K
              </kbd>
            </span>
          </button>

          <NotificationsPopover unreadCount={unreadCount} />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 rounded-md py-1 pl-1 pr-1.5 transition-colors hover:bg-white/10"
                aria-label="User menu"
              >
                <span
                  className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-semibold text-white"
                  style={{ background: "var(--primary)" }}
                >
                  {initials}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {userName && <DropdownMenuLabel>{userName}</DropdownMenuLabel>}
              {userEmail && (
                <div className="px-2 pb-2 text-[11px] text-muted-foreground">{userEmail}</div>
              )}
              {userRoleLabel && (
                <div className="mx-2 mb-1 inline-block rounded px-1.5 py-[1px] font-mono text-[10px] uppercase tracking-wide text-primary ring-1 ring-primary/25">
                  {userRoleLabel.replace("_", " ")}
                </div>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profile" className="cursor-pointer">
                  <UserCircle className="h-4 w-4" />
                  <span>Profile</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onSignOut} className="cursor-pointer">
                <LogOut className="h-4 w-4" />
                <span>Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <ProjectSearchPalette open={paletteOpen} onOpenChange={setPaletteOpen} role={role} />
    </>
  );
}
